/**
 * Analytics Service
 *
 * Microservice for generating analytics and statistics
 * Handles complex queries and calculations for dashboard metrics
 */

import { supabase, getCurrentUserId } from './supabaseClient';

/**
 * Get total monthly spend for the user
 * Normalizes all billing cycles to monthly amounts
 * @returns {Promise<number>} Total monthly spend
 */
export const getTotalMonthlySpend = async () => {
  try {
    const userId = await getCurrentUserId();

    const { data, error } = await supabase
      .from('subscriptions')
      .select('price, billing_cycle')
      .eq('user_id', userId)
      .eq('status', 'active');

    if (error) {
      console.error('Error calculating monthly spend:', error);
      throw error;
    }

    const total = data.reduce((sum, sub) => {
      const price = parseFloat(sub.price);
      let monthlyPrice = price;

      switch (sub.billing_cycle) {
        case 'weekly':
          monthlyPrice = price * 4.33; // Average weeks per month
          break;
        case 'monthly':
          monthlyPrice = price;
          break;
        case 'quarterly':
          monthlyPrice = price / 3;
          break;
        case 'yearly':
          monthlyPrice = price / 12;
          break;
        default:
          monthlyPrice = price;
      }

      return sum + monthlyPrice;
    }, 0);

    return parseFloat(total.toFixed(2));
  } catch (error) {
    console.error('getTotalMonthlySpend error:', error);
    throw error;
  }
};

/**
 * Get total yearly spend for the user
 * @returns {Promise<number>} Total yearly spend
 */
export const getTotalYearlySpend = async () => {
  try {
    const monthlySpend = await getTotalMonthlySpend();
    return parseFloat((monthlySpend * 12).toFixed(2));
  } catch (error) {
    console.error('getTotalYearlySpend error:', error);
    throw error;
  }
};

/**
 * Get subscription counts by status
 * @returns {Promise<Object>} Object with counts for each status
 */
export const getSubscriptionCounts = async () => {
  try {
    const userId = await getCurrentUserId();

    const { data, error } = await supabase
      .from('subscriptions')
      .select('status')
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching subscription counts:', error);
      throw error;
    }

    const counts = data.reduce((acc, sub) => {
      acc[sub.status] = (acc[sub.status] || 0) + 1;
      return acc;
    }, {});

    return {
      total: data.length,
      active: counts.active || 0,
      paused: counts.paused || 0,
      cancelled: counts.cancelled || 0
    };
  } catch (error) {
    console.error('getSubscriptionCounts error:', error);
    throw error;
  }
};

/**
 * Get spending breakdown by category
 * @returns {Promise<Array>} Array of categories with total spend
 */
export const getSpendingByCategory = async () => {
  try {
    const userId = await getCurrentUserId();

    const { data, error } = await supabase
      .from('subscriptions')
      .select(`
        price,
        billing_cycle,
        category:categories(id, name, emoji, color)
      `)
      .eq('user_id', userId)
      .eq('status', 'active');

    if (error) {
      console.error('Error fetching spending by category:', error);
      throw error;
    }

    // Group by category and calculate monthly spend
    const categoryMap = {};

    data.forEach(sub => {
      const categoryName = sub.category?.name || 'Uncategorized';
      const price = parseFloat(sub.price);
      let monthlyPrice = price;

      // Normalize to monthly
      switch (sub.billing_cycle) {
        case 'weekly':
          monthlyPrice = price * 4.33;
          break;
        case 'monthly':
          monthlyPrice = price;
          break;
        case 'quarterly':
          monthlyPrice = price / 3;
          break;
        case 'yearly':
          monthlyPrice = price / 12;
          break;
        default:
          monthlyPrice = price;
      }

      if (!categoryMap[categoryName]) {
        categoryMap[categoryName] = {
          category: categoryName,
          emoji: sub.category?.emoji || '📋',
          color: sub.category?.color || 'bg-gray-500',
          totalMonthly: 0,
          count: 0
        };
      }

      categoryMap[categoryName].totalMonthly += monthlyPrice;
      categoryMap[categoryName].count += 1;
    });

    // Convert to array and sort by spend
    return Object.values(categoryMap)
      .map(cat => ({
        ...cat,
        totalMonthly: parseFloat(cat.totalMonthly.toFixed(2))
      }))
      .sort((a, b) => b.totalMonthly - a.totalMonthly);
  } catch (error) {
    console.error('getSpendingByCategory error:', error);
    throw error;
  }
};

/**
 * Get spending breakdown by service/subscription
 * @param {number} limit - Number of top services to return (default: 10)
 * @returns {Promise<Array>} Array of subscriptions sorted by spend
 */
export const getTopSpendingServices = async (limit = 10) => {
  try {
    const userId = await getCurrentUserId();

    const { data, error } = await supabase
      .from('subscriptions')
      .select('name, price, billing_cycle, logo, category:categories(name, color)')
      .eq('user_id', userId)
      .eq('status', 'active')
      .order('price', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching top spending services:', error);
      throw error;
    }

    return data.map(sub => {
      const price = parseFloat(sub.price);
      let monthlyPrice = price;

      // Normalize to monthly
      switch (sub.billing_cycle) {
        case 'weekly':
          monthlyPrice = price * 4.33;
          break;
        case 'monthly':
          monthlyPrice = price;
          break;
        case 'quarterly':
          monthlyPrice = price / 3;
          break;
        case 'yearly':
          monthlyPrice = price / 12;
          break;
        default:
          monthlyPrice = price;
      }

      return {
        name: sub.name,
        price: parseFloat(sub.price),
        monthlyPrice: parseFloat(monthlyPrice.toFixed(2)),
        billingCycle: sub.billing_cycle,
        logo: sub.logo,
        category: sub.category?.name || 'Uncategorized',
        color: sub.category?.color || 'bg-gray-500'
      };
    });
  } catch (error) {
    console.error('getTopSpendingServices error:', error);
    throw error;
  }
};

/**
 * Get currency distribution
 * @returns {Promise<Array>} Array of currencies with counts and totals
 */
export const getCurrencyDistribution = async () => {
  try {
    const userId = await getCurrentUserId();

    const { data, error } = await supabase
      .from('subscriptions')
      .select('currency, price, status')
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching currency distribution:', error);
      throw error;
    }

    // Group by currency
    const currencyMap = {};

    data.forEach(sub => {
      const currency = sub.currency || 'USD';

      if (!currencyMap[currency]) {
        currencyMap[currency] = {
          currency,
          total: 0,
          count: 0,
          activeCount: 0
        };
      }

      const price = parseFloat(sub.price);
      currencyMap[currency].total += price;
      currencyMap[currency].count += 1;

      if (sub.status === 'active') {
        currencyMap[currency].activeCount += 1;
      }
    });

    return Object.values(currencyMap)
      .map(curr => ({
        ...curr,
        total: parseFloat(curr.total.toFixed(2))
      }))
      .sort((a, b) => b.activeCount - a.activeCount);
  } catch (error) {
    console.error('getCurrencyDistribution error:', error);
    throw error;
  }
};

/**
 * Get price change history and impact
 * By default only returns company-imposed price increases (not plan changes)
 * @param {boolean} includeAllTypes - If true, include plan_change and dismissed events
 * @returns {Promise<Object>} Price change statistics
 */
export const getPriceChangeAnalytics = async (includeAllTypes = false) => {
  try {
    const userId = await getCurrentUserId();

    let query = supabase
      .from('price_events')
      .select(`
        *,
        subscription:subscriptions(
          id,
          name,
          logo,
          user_id,
          category:categories(name, color)
        )
      `)
      .eq('subscription.user_id', userId)
      .order('change_date', { ascending: false });

    // Filter to only show price increases by default (not plan changes or dismissed)
    if (!includeAllTypes) {
      query = query.eq('event_type', 'price_increase');
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching price change analytics:', error);
      throw error;
    }

    // Filter out any records where subscription is null (deleted subscriptions)
    const validEvents = data.filter(event => event.subscription);

    // Only calculate totals from price increases (not plan changes)
    const priceIncreaseEvents = validEvents.filter(
      e => e.event_type === 'price_increase' || !e.event_type
    );

    const totalIncrease = priceIncreaseEvents.reduce((sum, event) => {
      return sum + parseFloat(event.change_amount || 0);
    }, 0);

    const averageIncrease = priceIncreaseEvents.length > 0
      ? totalIncrease / priceIncreaseEvents.length
      : 0;

    return {
      totalEvents: priceIncreaseEvents.length,
      totalIncrease: parseFloat(totalIncrease.toFixed(2)),
      averageIncrease: parseFloat(averageIncrease.toFixed(2)),
      events: validEvents.map(event => ({
        id: event.id,
        subscriptionId: event.subscription.id,
        subscriptionName: event.subscription.name,
        logo: event.subscription.logo,
        category: event.subscription.category?.name || 'Uncategorized',
        color: event.subscription.category?.color || 'bg-gray-500',
        oldPrice: parseFloat(event.old_price),
        newPrice: parseFloat(event.new_price),
        changeAmount: parseFloat(event.change_amount),
        changePercentage: parseFloat(event.change_percentage),
        changeDate: event.change_date,
        eventType: event.event_type || 'price_increase',
        userOverride: event.user_override || false
      }))
    };
  } catch (error) {
    console.error('getPriceChangeAnalytics error:', error);
    throw error;
  }
};

/**
 * Update price event classification (dismiss or reclassify)
 * @param {string} eventId - The price event ID
 * @param {string} newEventType - 'price_increase', 'plan_change', or 'dismissed'
 * @returns {Promise<Object>} Updated event
 */
export const updatePriceEventClassification = async (eventId, newEventType) => {
  try {
    const userId = await getCurrentUserId();

    // First verify the event belongs to the user
    const { data: event, error: fetchError } = await supabase
      .from('price_events')
      .select(`
        *,
        subscription:subscriptions(user_id)
      `)
      .eq('id', eventId)
      .single();

    if (fetchError) {
      console.error('Error fetching price event:', fetchError);
      throw fetchError;
    }

    if (event.subscription.user_id !== userId) {
      throw new Error('Unauthorized: Event does not belong to user');
    }

    // Update the classification
    const { data, error } = await supabase
      .from('price_events')
      .update({
        event_type: newEventType,
        user_override: true,
        override_date: new Date().toISOString()
      })
      .eq('id', eventId)
      .select()
      .single();

    if (error) {
      console.error('Error updating price event classification:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('updatePriceEventClassification error:', error);
    throw error;
  }
};

/**
 * Get monthly trend data
 * Simulates historical data based on current subscriptions
 * @param {number} months - Number of months to generate (default: 12)
 * @returns {Promise<Array>} Array of monthly trend data
 */
export const getMonthlyTrend = async (months = 12) => {
  try {
    const userId = await getCurrentUserId();

    const { data, error } = await supabase
      .from('subscriptions')
      .select('price, billing_cycle, subscription_date, status')
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching monthly trend data:', error);
      throw error;
    }

    // Generate trend data for the last N months
    const trend = [];
    const today = new Date();

    for (let i = months - 1; i >= 0; i--) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthName = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

      // Calculate active subscriptions for that month
      const activeSubs = data.filter(sub => {
        const subDate = new Date(sub.subscription_date);
        return subDate <= date && sub.status !== 'cancelled';
      });

      const monthlySpend = activeSubs.reduce((sum, sub) => {
        const price = parseFloat(sub.price);
        let monthlyPrice = price;

        switch (sub.billing_cycle) {
          case 'weekly':
            monthlyPrice = price * 4.33;
            break;
          case 'monthly':
            monthlyPrice = price;
            break;
          case 'quarterly':
            monthlyPrice = price / 3;
            break;
          case 'yearly':
            monthlyPrice = price / 12;
            break;
          default:
            monthlyPrice = price;
        }

        return sum + monthlyPrice;
      }, 0);

      trend.push({
        month: monthName,
        spend: parseFloat(monthlySpend.toFixed(2)),
        subscriptions: activeSubs.length
      });
    }

    return trend;
  } catch (error) {
    console.error('getMonthlyTrend error:', error);
    throw error;
  }
};

/**
 * Get comprehensive dashboard statistics
 * @returns {Promise<Object>} All dashboard statistics
 */
export const getDashboardStats = async () => {
  try {
    const [
      monthlySpend,
      yearlySpend,
      counts,
      categoryBreakdown,
      topServices
    ] = await Promise.all([
      getTotalMonthlySpend(),
      getTotalYearlySpend(),
      getSubscriptionCounts(),
      getSpendingByCategory(),
      getTopSpendingServices(5)
    ]);

    const averageCost = counts.active > 0
      ? monthlySpend / counts.active
      : 0;

    return {
      totalMonthlySpend: monthlySpend,
      totalYearlySpend: yearlySpend,
      totalSubscriptions: counts.total,
      activeCount: counts.active,
      pausedCount: counts.paused,
      cancelledCount: counts.cancelled,
      averageCost: parseFloat(averageCost.toFixed(2)),
      categoryBreakdown,
      topServices
    };
  } catch (error) {
    console.error('getDashboardStats error:', error);
    throw error;
  }
};

export default {
  getTotalMonthlySpend,
  getTotalYearlySpend,
  getSubscriptionCounts,
  getSpendingByCategory,
  getTopSpendingServices,
  getCurrencyDistribution,
  getPriceChangeAnalytics,
  getMonthlyTrend,
  getDashboardStats
};
